<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear
        absolute
        color="success"
        height="4"
        indeterminate
      ></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="crm-color" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ $t("COMPANIES.TITLE") }}</v-toolbar-title>
      </v-tab>
      <v-spacer></v-spacer>
      <div class="buttons-tab">
        <v-btn
          v-if="canOpenFilteredContacts"
          class="mr-4 warning"
          :disabled="isProcessing || isOpeningContacts"
          :loading="isOpeningContacts"
          @click="handleOpenFilteredContacts"
        >
          {{ $t("COMPANIES.OPEN_CONTACTS") }}
        </v-btn>
        <v-btn
          v-if="hasPermissions('companies-create')"
          class="mr-4 success"
          :disabled="isProcessing"
          @click="handleCreateClick"
        >
          {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
        </v-btn>
      </div>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <v-card-text class="pa-0">
            <Table
              v-if="userStatePrepared"
              ref="companies-table"
              height="500"
              name="companies-table"
              :headers="headers"
              :hidden-headers="hiddenHeaders"
              :filters="filters"
              :blink-row-item-key="blinkRowItemKey"
              :server-data-options="{
                method: tableItemsMethod
              }"
              :items-per-page="100"
              :loading="isProcessing"
              dense
              fixed-header
              multi-sort
              filterable
              @filters-change="handleFiltersChange"
              @search="handleSearchChange"
            >
              <template
                v-for="(header, index) in columnHeaders"
                v-slot:[`item.${header.value}`]="{ item }"
              >
                <TableRowActions
                  v-if="header.value === 'options'"
                  :key="index"
                  :show-delete="false"
                >
                  <template #view>
                    <v-btn
                      icon
                      color="indigo"
                      :to="{ name: 'companies-view', params: { id: item.item.id } }"
                      @click="rememberTableRow(item.item.id)"
                    >
                      <v-icon dark>
                        mdi-eye
                      </v-icon>
                    </v-btn>
                  </template>
                </TableRowActions>
                <template v-else-if="header.value === 'active'">
                  <v-chip
                    x-small
                    :color="item.value ? 'success' : 'error'"
                    dark
                  >
                    {{ item.value ? $t("GENERAL.LABELS.ACTIVE") : $t("GENERAL.LABELS.INACTIVE") }}
                  </v-chip>
                </template>
                <template v-else-if="header.value === 'types'">
                  {{ item.value?.map(type => type.name).join(", ") }}
                </template>
                <template v-else-if="header.value === 'rank'">
                  {{ item.value?.name ?? "-" }}
                </template>
                <template v-else-if="header.value === 'country'">
                  {{ item.value?.label ?? item.value?.name ?? "-" }}
                </template>
                <template v-else-if="header.value === 'owner'">
                  {{ item.value?.name ?? "-" }}
                </template>
                <template v-else-if="header.value === 'information'">
                  <template v-if="item.item.information?.length">
                    <FieldTagChip
                      v-for="tag in item.item.information"
                      :key="tag.id || tag.value"
                      :tag="tag"
                    />
                  </template>
                  <span v-else>-</span>
                </template>
                <template v-else-if="header.value === 'company_contacts_count'">
                  <router-link
                    v-if="item.item.id && hasPermissions('company-contacts-view-all')"
                    :to="{ name: 'company-contacts', query: { company_id: item.item.id } }"
                    class="primary--text"
                  >
                    {{ item.value ?? 0 }}
                  </router-link>
                  <span v-else>{{ item.value ?? 0 }}</span>
                </template>
                <template v-else>
                  {{ item.value ?? "-" }}
                </template>
              </template>
            </Table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import TableRowActions from "@/views/partials/v-table/components/TableRowActions.vue";
import {
  GET_COMPANIES,
  GET_COMPANY_TYPES_OPTIONS,
  GET_COMPANY_RANKS_OPTIONS,
} from "@/store/companies.module";
import { GET_COUNTRIES } from "@/store/countries.module";
import { GET_USERS_OPTIONS } from "@/store/users.module";
import { SHOW_SNACK } from "@/store/snack.module";
import userStateService from "@/common/userState.service.js";
import FieldTagChip from "@/views/partials/fieldTags/FieldTagChip.vue";

export default {
  name: "ListCompanies",
  components: {
    Table,
    TableRowActions,
    FieldTagChip,
  },
  data() {
    return {
      tableName: "companies-table",
      contactsTableName: "company-contacts-table",
      maxContactsFilterCompanies: 50,
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      isOpeningContacts: false,
      serverFilters: {},
      searchString: "",
      tableItemsMethod: GET_COMPANIES,
      headers: [
        {
          text: "#",
          value: "id",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("GENERAL.LABELS.NAME"),
          value: "name",
          align: "center",
          width: "20%",
        },
        {
          text: this.$t("GENERAL.LABELS.CONTACTS"),
          value: "company_contacts_count",
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("GENERAL.LABELS.STATUS"),
          value: "active",
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("GENERAL.TABLE.OPTIONS_COLUMN"),
          value: "options",
          sortable: false,
          align: "center",
          width: "10%",
        },
      ],
      hiddenHeaders: [
        {
          text: this.$t("COMPANIES.VAT"),
          value: "vat",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("GENERAL.LABELS.COUNTRY"),
          value: "country",
          align: "center",
          width: "12%",
        },
        {
          text: "Types",
          value: "types",
          align: "center",
          width: "12%",
          sortable: false,
        },
        {
          text: this.$t("COMPANIES.CREDIT_LIMIT"),
          value: "credit_limit",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("COMPANIES.WEBSITE"),
          value: "website",
          align: "center",
          width: "12%",
        },
        {
          text: this.$t("COMPANIES.ADDRESS"),
          value: "address",
          align: "center",
          width: "15%",
        },
        {
          text: this.$t("COMPANIES.NOTES"),
          value: "notes",
          align: "center",
          width: "15%",
        },
        {
          text: this.$t("COMPANIES.INFORMATION"),
          value: "information",
          align: "center",
          width: "12%",
          sortable: false,
        },
        {
          text: this.$t("COMPANIES.OWNED_BY"),
          value: "owner",
          align: "center",
          width: "12%",
        },
        {
          text: "Rank",
          value: "rank",
          align: "center",
          width: "10%",
        },
      ],
      filters: [
        {
          key: "active",
          label: this.$t("GENERAL.LABELS.STATUS"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [
            { id: 1, label: this.$t("GENERAL.LABELS.ACTIVE") },
            { id: 0, label: this.$t("GENERAL.LABELS.INACTIVE") },
          ],
        },
        {
          key: "company_type_id",
          label: "Types",
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "company_rank_id",
          label: "Rank",
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "owned_by",
          label: this.$t("COMPANIES.OWNED_BY"),
          type: "select",
          "item-value": "id",
          "item-text": "name",
          items: [],
        },
        {
          key: "country_id",
          label: this.$t("GENERAL.LABELS.COUNTRY"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: [],
        },
        {
          key: "information_field_tag_id",
          label: this.$t("COMPANIES.INFORMATION"),
          type: "field_tag",
          field_tag_type_slug: "company-information",
          items: [],
        },
      ],
    };
  },
  computed: {
    columnHeaders() {
      return [...this.headers, ...this.hiddenHeaders];
    },
    hasActiveFilters() {
      if (this.searchString) {
        return true;
      }

      return Object.values(this.serverFilters || {}).some((value) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }

        return value !== undefined && value !== null && value !== "";
      });
    },
    canOpenFilteredContacts() {
      return this.hasActiveFilters && this.hasPermissions("company-contacts-view-all");
    },
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );

    this.serverFilters = userStateService.getState(
      `table-state.${this.tableName}.serverFilters`,
      {}
    );
    this.searchString = userStateService.getState(
      `table-state.${this.tableName}.searchString`,
      ""
    );

    const selectedHiddenHeadersKey = `table-state.${this.tableName}.selectedHiddenHeaders`;
    const selectedHiddenHeadersInitializedKey = `table-state.${this.tableName}.selectedHiddenHeadersInitialized`;
    const informationColumnKey = `table-state.${this.tableName}.informationColumnAdded`;

    if (!userStateService.getState(selectedHiddenHeadersInitializedKey, false)) {
      userStateService.setState(selectedHiddenHeadersKey, [...this.hiddenHeaders]);
      userStateService.setState(selectedHiddenHeadersInitializedKey, true);
      userStateService.setState(informationColumnKey, true);
    } else if (!userStateService.getState(informationColumnKey, false)) {
      const selected = userStateService.getState(selectedHiddenHeadersKey, []);
      const informationHeader = this.hiddenHeaders.find((header) => header.value === "information");

      if (
        informationHeader
        && !selected.some((header) => header.value === "information")
      ) {
        userStateService.setState(selectedHiddenHeadersKey, [...selected, informationHeader]);
      }

      userStateService.setState(informationColumnKey, true);
    }

    this.loadFilterOptions().finally(() => {
      this.userStatePrepared = true;
    });
  },
  methods: {
    handleFiltersChange(filters) {
      this.serverFilters = filters || {};
    },
    handleSearchChange(searchString) {
      this.searchString = searchString && searchString.length > 2 ? searchString : "";
    },
    handleOpenFilteredContacts() {
      if (!this.canOpenFilteredContacts || this.isOpeningContacts) {
        return;
      }

      const table = this.$refs["companies-table"];
      const sortBy = table?.options?.sortBy || [];
      const sortDesc = table?.options?.sortDesc || [];

      this.isOpeningContacts = true;

      this.$store
        .dispatch(GET_COMPANIES, {
          page: 1,
          itemsPerPage: this.maxContactsFilterCompanies,
          search: this.searchString,
          filters: this.serverFilters,
          sortBy,
          sortDesc,
        })
        .then((response) => {
          const total = response.meta?.total ?? (response.data || []).length;

          if (total > this.maxContactsFilterCompanies) {
            this.$store.dispatch(SHOW_SNACK, {
              type: "error",
              message: this.$t("COMPANIES.TOO_MANY_COMPANIES_FOR_CONTACTS", {
                max: this.maxContactsFilterCompanies,
              }),
            });
            return;
          }

          const companies = response.data || [];
          const companyIds = companies.map((company) => company.id).filter(Boolean);

          if (!companyIds.length) {
            this.$store.dispatch(SHOW_SNACK, {
              type: "error",
              message: this.$t("COMPANIES.NO_COMPANIES_FOR_CONTACTS"),
            });
            return;
          }

          const filterLabel = companyIds.length === 1
            ? (companies[0].name || `Id: ${companyIds[0]}`)
            : this.$t("COMPANIES.FILTERED_COMPANIES_COUNT", { count: companyIds.length });

          userStateService.setState(`table-state.${this.contactsTableName}.searchString`, "");
          userStateService.setState(`table-state.${this.contactsTableName}.serverFilters`, {
            company_id: companyIds,
          });
          userStateService.setState(`table-state.${this.contactsTableName}.filterLabels`, {
            company_id: filterLabel,
          });

          this.$router.push({ name: "company-contacts" });
        })
        .catch((error) => {
          this.$store.dispatch(SHOW_SNACK, {
            type: "error",
            message: this.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.isOpeningContacts = false;
        });
    },
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];

        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }

      if (error && error.message) {
        return error.message;
      }

      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    loadFilterOptions() {
      return Promise.all([
        this.$store.dispatch(GET_COMPANY_TYPES_OPTIONS, {}),
        this.$store.dispatch(GET_COMPANY_RANKS_OPTIONS, {}),
        this.$store.dispatch(GET_USERS_OPTIONS),
        this.$store.dispatch(GET_COUNTRIES),
      ])
        .then(([companyTypesResponse, companyRanksResponse, usersResponse, countriesResponse]) => {
          this.filters[1].items = companyTypesResponse.data || [];
          this.filters[2].items = companyRanksResponse.data || [];
          this.filters[3].items = usersResponse || [];
          this.filters[4].items = Array.isArray(countriesResponse)
            ? countriesResponse
            : (countriesResponse.data || []);
        })
        .catch(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        });
    },
    handleCreateClick() {
      this.$router.push({ name: "companies-create" });
    },
    rememberTableRow(id) {
      userStateService.setState(
        `table-state.${this.tableName}.blinkRowItemKey`,
        id
      );
    },
  },
};
</script>
